/* Crossfire's hero (2 Aug 2026, Rob): the same slideshow Discover and the
   feed run, over the disagreements the reader has not answered yet.
   The whole picture opens the crossfire modal, which is where a question is
   read and where a stance is recorded, so the hero adds a way in and no new
   way to decide.

   What it shows that the other heroes do not: two people, facing. The
   argument is the content here, and a question with a single portrait on it
   would be the one thing this screen must never imply, which is that a
   disagreement has an owner. So the slide carries the two named positions,
   and the count when there are more than two, and never a position's text:
   quoting one side under a picture is taking a side with the layout.

   The question is the line, so the question sets the hold, which is what the
   engine already does with a practice or a headline.
*/
import { useEffect, useRef, useState } from "react";
import { useStore } from "../state/store.js";
import type { CrossfireQuestionView } from "../state/types.js";
import { Avatar } from "./Avatar.js";
import { XF_FALLBACK } from "./placeholderArt.js";
import { HERO_SIZES, shuffled, useScrolledPast, useSlideshow, type Slide } from "./slideshow.js";
import { artImg } from "../assets.js";

interface XfSlide extends Slide {
  questionId: string;
  expertIds: string[];
  positions: number;
}

// Matches Discover's deck: about ninety seconds of material, and never more
// pictures held than one screen will use.
const DECK_SIZE = 12;

function draw(
  questions: CrossfireQuestionView[],
  exclude: Set<string> = new Set(),
  room = DECK_SIZE,
): XfSlide[] {
  const pool = questions
    .filter((q) => !exclude.has(q.id))
    .map((q) => {
      const named = q.lanes.filter((l) => l.expertId);
      return {
        id: q.id,
        questionId: q.id,
        text: q.question,
        image: q.image ?? XF_FALLBACK,
        expertIds: named.map((l) => l.expertId as string),
        positions: named.length,
      };
    });
  return shuffled(pool).slice(0, room);
}

export function CrossfireHero({ questions }: { questions: CrossfireQuestionView[] }) {
  const openSheet = useStore((s) => s.openSheet);
  const experts = useStore((s) => s.experts) ?? [];
  const sheetOpen = useStore((s) => s.sheet !== null);
  const stage = useRef<HTMLDivElement>(null);
  const scrolledPast = useScrolledPast(stage);

  /* Drawn once at mount and then trimmed, never reshuffled: a deck rebuilt on
     every render would not hold a slide still long enough to read.

     Trimming is the whole point (2 Aug 2026, Rob: "just not show them cards
     they have already decided on"). The screen hands this the New lane, so a
     question that gets an answer leaves that set, but a ref captured at mount
     would keep showing it: the reader records a stance in the modal, closes
     it, and the hero is still asking. Discover's hero has done this since it
     shipped; this one had not. Topping up from what is left keeps a long
     session finding fresh material instead of grinding to the last two. */
  const [deck, setDeck] = useState<XfSlide[]>(() => draw(questions));
  const ids = questions.map((q) => q.id).join("|");
  useEffect(() => {
    setDeck((prev) => {
      const live = new Set(questions.map((q) => q.id));
      const kept = prev.filter((sl) => live.has(sl.questionId));
      if (kept.length === prev.length && kept.length >= DECK_SIZE) return prev;
      const top = draw(questions, new Set(kept.map((sl) => sl.questionId)), DECK_SIZE - kept.length);
      if (!top.length && kept.length === prev.length) return prev;
      return [...kept, ...top];
    });
  }, [ids, questions]);

  const { cur, prev, corner } = useSlideshow(deck, {
    paused: sheetOpen || scrolledPast,
  });
  if (!cur) return null;

  const faces = cur.expertIds
    .map((id) => experts.find((e) => e.id === id))
    .filter((e): e is NonNullable<typeof e> => Boolean(e));
  const open = () => openSheet({ kind: "crossfire", questionId: cur.questionId });

  return (
    <div className="hslides apphero xfhero" ref={stage} onClick={open}>
      {prev && (
        <figure className="hslide out" key={`${prev.id}-out`} aria-hidden="true">
          <img className="hslide-img" alt="" {...artImg(prev.image)} sizes={HERO_SIZES} />
        </figure>
      )}
      <figure className="hslide" key={cur.id} data-pos={corner}>
        <img className="hslide-img" alt="" {...artImg(cur.image)} sizes={HERO_SIZES} />
        <figcaption>
          <span className="hslide-cap">
            <span className="hslide-who">
              {/* Faces only, no names: two or three names in a row under a
                  photograph is a credit list, and the names are on every lane
                  inside. Overlapped, because they are one group, the people
                  in this argument, rather than a sequence. */}
              <span className="xfhero-faces">
                {faces.slice(0, 4).map((e) => (
                  <Avatar key={e.id} e={e} size="m" />
                ))}
              </span>
              <span className="hslide-idn">
                <span className="hslide-meta" data-trail>
                  {/* The count and nothing else (2 Aug 2026, Rob). "No winner
                      picked" was saying out loud what the screen already does
                      by never ordering or emphasising a side, and a promise
                      repeated on every slide reads as a defence. */}
                  <span className="hslide-grade">
                    {cur.positions} position{cur.positions === 1 ? "" : "s"}
                  </span>
                </span>
              </span>
            </span>
            {/* The question, set as the slide's line. A question mark is its
                own punctuation, so it does not take sentence()'s full stop. */}
            <button
              className="hslide-do"
              onClick={(ev) => {
                ev.stopPropagation();
                open();
              }}
            >
              {cur.text}
            </button>
          </span>
        </figcaption>
      </figure>
    </div>
  );
}
